import { GenerateRangesRequest, ReportConfiguration, ReportTimeframes, StartEndDates } from '@hone-automation/common';
import { generatePeriodRanges } from '@hone-automation/common/lib/reportUtil';
import { WeekDay } from '@hone-automation/common/lib/commonUtil';

export interface DateMapping {
  originalDate: string;
  convertedDate: string;
  periodName?: string;
  periodEnd?: string;
}

export interface BudgetCsvTransformResult {
  success: boolean;
  csv: string;
  error?: string;
  wasConverted: boolean;
  dateMappings?: DateMapping[];
  isPeriodLocation?: boolean;
}

/**
 * Sanitizes a date string from the CSV header:
 * - Fixes 2-digit years (e.g., 1/1/26 → 1/1/2026)
 */
function sanitizeDateString(dateStr: string): string {
  const trimmed = dateStr.trim();
  const parts = trimmed.split('/');
  if (parts.length === 3) {
    const year = parseInt(parts[2], 10);
    if (year >= 0 && year < 100) {
      parts[2] = String(year + 2000);
    }
    return parts.join('/');
  }
  return trimmed;
}

/**
 * Parses a M/D/YYYY date string into a Date object.
 */
function parseDateHeader(dateStr: string): Date | null {
  const sanitized = sanitizeDateString(dateStr);
  const parts = sanitized.split('/');
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
  const date = new Date(year, month - 1, day);
  // Reject dates that rolled over (e.g., 2/30 → 3/2)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * Formats a YYYY-MM-DD short date string to M/D/YYYY for CSV headers.
 */
function shortDateToHeaderFormat(shortDate: string): string {
  const [year, month, day] = shortDate.split('-').map(Number);
  return `${month}/${day}/${year}`;
}

/**
 * Checks if all dates in the header are 1st-of-month dates.
 */
function areAllFirstOfMonth(dates: Date[]): boolean {
  return dates.every(d => d.getDate() === 1);
}

/**
 * Computes the expected period start dates for a period location using generatePeriodRanges.
 * Returns the ranges that overlap with the fiscal year containing the CSV dates.
 */
function getExpectedPeriodDates(
  reportConfig: ReportConfiguration,
  weekStart: WeekDay,
  csvDates: Date[],
  expectedCount: number
): StartEndDates[] {
  const configData = reportConfig.data;
  const earliestCsvDate = csvDates.reduce((a, b) => (a < b ? a : b));
  const latestCsvDate = csvDates.reduce((a, b) => (a > b ? a : b));

  // Set the start before the earliest CSV date to ensure we capture the full fiscal year
  const startYear = earliestCsvDate.getFullYear();
  const startDate = `${startYear - 1}-01-01`;
  // End after the latest CSV date
  const endYear = latestCsvDate.getFullYear();
  const endDate = `${endYear + 1}-12-31`;

  const request = Object.assign(new GenerateRangesRequest(ReportTimeframes.Month, weekStart, configData), {
    start: startDate,
    end: endDate,
    includeNames: true,
  });

  const allRanges = generatePeriodRanges(request);

  // Find the 12 consecutive periods that best match the CSV date range
  // Fiscal years can span two calendar years (e.g., Dec 2025 – Dec 2026)
  const targetYear = earliestCsvDate.getFullYear();
  const relevantRanges = allRanges
    .filter(r => {
      const rangeYear = new Date(r.start).getFullYear();
      return rangeYear >= targetYear - 1 && rangeYear <= targetYear + 1;
    })
    .sort((a, b) => a.start.localeCompare(b.start));

  // Take the N periods closest to the CSV dates (12 for 5-4-4/4-4-5, 13 for 4-4-4)
  if (relevantRanges.length >= expectedCount) {
    // Find the best starting index: the one where the first range start is closest to the first CSV date
    const csvFirstTime = earliestCsvDate.getTime();
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i <= relevantRanges.length - expectedCount; i++) {
      const rangeStart = new Date(relevantRanges[i].start).getTime();
      const diff = Math.abs(rangeStart - csvFirstTime);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    return relevantRanges.slice(bestIdx, bestIdx + expectedCount);
  }

  return relevantRanges;
}

/**
 * Parses a CSV string into lines, handling quoted fields with commas.
 */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Transforms a budget CSV for period locations:
 * 1. Sanitizes dates (fixes 2-digit years)
 * 2. If the location uses period reporting and dates aren't 1st-of-month,
 *    replaces the CSV header dates with the computed period start dates
 * 3. Validates column count
 *
 * For monthly locations, only sanitizes dates.
 */
export function transformBudgetCsv(
  csvContent: string,
  reportConfig: ReportConfiguration | undefined,
  weekStart: WeekDay | undefined
): BudgetCsvTransformResult {
  const lines = csvContent.split('\n').filter(line => line.trim().length > 0);

  if (lines.length < 2) {
    return { success: false, csv: csvContent, error: 'CSV file is empty or has no data rows.', wasConverted: false };
  }

  const headerFields = parseCsvLine(lines[0]);
  // First two fields are labels (empty + "Period start"), rest are dates
  const dateFields = headerFields.slice(2);

  if (dateFields.length === 0) {
    return { success: false, csv: csvContent, error: 'No date columns found in CSV header.', wasConverted: false };
  }

  // Sanitize all dates (fix 2-digit years)
  const sanitizedDateStrings = dateFields.map(sanitizeDateString);
  const parsedDates = sanitizedDateStrings.map(parseDateHeader);

  const invalidDateIdx = parsedDates.findIndex(d => d === null);
  if (invalidDateIdx !== -1) {
    return {
      success: false,
      csv: csvContent,
      error: `Invalid date in column ${invalidDateIdx + 3}: "${dateFields[invalidDateIdx]}".`,
      wasConverted: false,
    };
  }

  const validDates = parsedDates as Date[];
  const isPeriodLocation = reportConfig?.data?.periodReporting === true;

  // If not a period location, just sanitize dates and return
  if (!isPeriodLocation) {
    const newHeader = [...headerFields.slice(0, 2), ...sanitizedDateStrings].join(',');
    const newLines = [newHeader, ...lines.slice(1)];
    return { success: true, csv: newLines.join('\n'), wasConverted: false };
  }

  // Period location: compute expected dates and replace
  if (!weekStart) {
    return {
      success: false,
      csv: csvContent,
      error: 'Location weekStart is required for period date validation.',
      wasConverted: false,
    };
  }

  // 4-4-4 configurations produce 13 periods (extra period in Q4)
  if (dateFields.length !== 12 && dateFields.length !== 13) {
    return {
      success: false,
      csv: csvContent,
      error: `Expected 12 or 13 period columns but found ${dateFields.length}.`,
      wasConverted: false,
    };
  }

  const expectedPeriodCount = dateFields.length;
  const expectedRanges = getExpectedPeriodDates(reportConfig, weekStart, validDates, expectedPeriodCount);
  if (expectedRanges.length !== expectedPeriodCount) {
    return {
      success: false,
      csv: csvContent,
      error: `Could not compute ${expectedPeriodCount} fiscal periods for this location. Got ${expectedRanges.length} periods. Please verify the location's period configuration.`,
      wasConverted: false,
    };
  }

  // If dates are already 1st-of-month for a period location, check whether they actually match
  // the fiscal period start dates. Only convert + show preview when a real change is detected.
  if (areAllFirstOfMonth(validDates)) {
    const correctedDateHeaders = expectedRanges.map(r => shortDateToHeaderFormat(r.start));
    const hasChanges = correctedDateHeaders.some((h, i) => h !== sanitizedDateStrings[i]);

    if (!hasChanges) {
      // Dates already match fiscal period starts — pass through, no preview needed
      const newHeader = [...headerFields.slice(0, 2), ...sanitizedDateStrings].join(',');
      const newLines = [newHeader, ...lines.slice(1)];
      return { success: true, csv: newLines.join('\n'), wasConverted: false, isPeriodLocation: true };
    }

    // Dates differ from fiscal periods — keep original dates but provide dateMappings for preview
    const newHeader = [...headerFields.slice(0, 2), ...sanitizedDateStrings].join(',');
    const newLines = [newHeader, ...lines.slice(1)];
    const dateMappings: DateMapping[] = sanitizedDateStrings.map((originalDate, i) => ({
      originalDate,
      convertedDate: correctedDateHeaders[i],
      periodName: (expectedRanges[i] as any).name || `P${String(i + 1).padStart(2, '0')}`,
      periodEnd: shortDateToHeaderFormat(expectedRanges[i].end),
    }));
    return { success: true, csv: newLines.join('\n'), wasConverted: false, dateMappings, isPeriodLocation: true };
  }

  // Validate that data rows have the right number of columns
  for (let i = 1; i < lines.length; i++) {
    const rowFields = parseCsvLine(lines[i]);
    // Skip the "GL Accounts" label row
    if (rowFields.length <= 2 && lines[i].replace(/,/g, '').trim() === 'GL Accounts') {
      continue;
    }
    if (rowFields.length !== headerFields.length) {
      return {
        success: false,
        csv: csvContent,
        error: `Row ${i + 1} has ${rowFields.length} columns but header has ${headerFields.length}.`,
        wasConverted: false,
      };
    }
  }

  // Replace header dates with computed period start dates
  const correctedDateHeaders = expectedRanges.map(r => shortDateToHeaderFormat(r.start));
  const newHeader = [...headerFields.slice(0, 2), ...correctedDateHeaders].join(',');
  const newLines = [newHeader, ...lines.slice(1)];

  // Build date mappings for preview
  const dateMappings: DateMapping[] = sanitizedDateStrings.map((originalDate, i) => ({
    originalDate,
    convertedDate: correctedDateHeaders[i],
    periodName: (expectedRanges[i] as any).name || `P${String(i + 1).padStart(2, '0')}`,
    periodEnd: shortDateToHeaderFormat(expectedRanges[i].end),
  }));

  return { success: true, csv: newLines.join('\n'), wasConverted: true, dateMappings, isPeriodLocation: true };
}
